import * as Sentry from '@sentry/browser';

import {
  SimpleLockManager,
  SimpleLockOptions,
} from 'polyfills/navigator.locks';

export const getUuidLockName = uuid => `pos_brazil_idb_uuid_${uuid}`;

const WebLocksAvailable: Promise<SimpleLockManager> =
  'locks' in navigator
    ? Promise.resolve((navigator.locks as unknown) as SimpleLockManager)
    : new Promise(resolve => {
        Sentry.setContext('WebLocksApiMissing', {
          solution:
            'Replaced by naive implementation (does not support options except ifAvailable, only works in current page)',
        });
        const lockManager = new SimpleLockManager();
        resolve(lockManager);
      });

type LockOptionsOrExisting = SimpleLockOptions & { existingLock?: Lock };

type Callback<Return> = (l: Lock | null) => Return;

// Promise unwrapping necessary to not return Promise<Promise<R>>
// Fixed in typescript 4.5.0, but we use 3 so we need to do this manually
export function reentrantWebLocksRequest<Return>(
  lockName: string,
  callback: Callback<Return>,
): Promise<Return extends Promise<infer R> ? R : Return>;
export function reentrantWebLocksRequest<Return>(
  lockName: string,
  options: LockOptionsOrExisting,
  callback: Callback<Return>,
): Promise<Return extends Promise<infer R> ? R : Return>;

/**
 * @typedef reentrantWebLocksRequest.docs.existingLock
 * @description
 * ## Motivation
 * There are often functions at multiple levels of abstraction that need access to the same lock.
 * Because web locks are not reentrant, it is not possible for both functions to request a lock *and* for the lower level function to be reused in the higher-level one
 * For example:
 * ```js
 * // Just deletes a record from IDB
 * export function deleteRecord(uuid) {
 *   navigator.locks.request(uuid, () => { // will deadlock if uuid is already locked by the caller
 *     ...
 *   })
 * }
 * export function deleteSalesDocument(uuid) {
 *   navigator.locks.request(uuid, () => {
 *     const record = await getSalesDocument(uuid) // will deadlock
 *     if (isSynced(record)) {
 *       await addDeleteRequest(uuid) // will deadlock
 *     } else {
 *       await deleteRecord(uuid) // will deadlock
 *     }
 *   })
 * }
 * ```
 * To avoid the deadlock, you would have to either
 * <ul>
 *   <li>Duplicate the code of the lower level functions in the higher level ones (very WET code)</li>
 *   <li>Not request locks in the higher level functions (This loses the safety that the locks provide in the first place)</li>
 *   <li>
 *     Not request locks in the lower level functions
 *     <ul>
 *     <li>And only export the highest-level functions, making the more granular ones inaccessible</li>
 *     <li>And export all, requiring the caller to always check the source code to know if they should request the lock themselves or not</li>
 *     </ul>
 *   </li>
 * </ul>
 *
 * Instead, using this method, functions can retain safety by requesting the lock by default, but skipping it if the caller has already done it.
 * Since there's no way to pass 'context' down function calls implicitly, the caller must explicitly prove they have the lock by passing the lock down as a second argument
 *
 * @example
 * // An exported function using this helper
 * function deleteSalesDocument(uuid, lock) {
 *   runWithLockifNotProvided(uuid, {mode: 'exclusive'}, lock, () => {
 *     ...
 *   })
 * }
 * // How it would look like without the helper
 * function deleteSalesDocument(uuid, lock) {
 *   const fn = () => {
 *     // ... 100 lines
 *   }
 *   if (lock) {
 *     fn()
 *   } else {
 *     navigator.locks.request(uuid, {mode: 'exclusive'}, fn)
 *   }
 * }
 *
 * // Normal usage
 * dispatch(deleteSalesDocument('1234')) // deleteSalesDocument will request a lock, safety is automatic
 *
 * // Usage with existing lock
 * navigator.locks.request(getUUIDLockName('1234'), {mode: 'exclusive'}, async (lock) => {
 *   ...
 *   // Prove we already requested a lock by passing it down
 *   dispatch(deleteSalesDocument('1234', lock))
 *   ...
 * })
 */

/**
 * @typedef reentrantWebLocksRequest.
 *
 * @description
 * When a web lock request is pending in the main thread, and it gets granted during a print, the request will fail.
 * This is probably a bug in the browser, but absent a fix (or even a clear ticket) we have to work around the bug ourselves.
 *
 * Since the error only happens after resuming from a pause, we simply request the lock again when the error is detected.
 *
 * Even if two prints are queued one after the other, the second request seems to succeed
 * @example
 * // Reproducing the issue is trivial, even without web workers.
 * // Request lock (it will be granted asynchronously in the next task
 * navigator.locks.request('test', () => {}).then(console.log, console.error)
 * // do a print (it will pause JS execution, preventing the request from being granted
 * window.print()
 * // After the user closes the print dialog, the request will be rejected
 */

/**
 * Same as {@link LockManager.request}, but with a couple additional fixes
 *
 * # It adds a new option "existingLock"
 *
 * If an existing lock is provided, and it matches the requirements, then the request is skipped and the callback is run normally
 * This allows for "reentrant" behaviour, to use function composition without causing deadlocks between different levels
 *
 * Read more: {@link reentrantWebLocksRequest.docs.existingLock|about existingLock param}
 *
 * # It automatically retries on 'LockGrantedCallback'
 *
 * If a 'LockGrantedCallback' error is encountered (caused by lock being granted during JS pause, such as window.print()),
 * then will automatically request the same lock again. This second attempt should almost always go through
 *
 * Read more: {@link reentrantWebLocksRequest.docs.lockGrantedCallback|about LockGrantedCallback issue}
 */
export function reentrantWebLocksRequest<Return>(
  lockName,
  options,
  callback?,
): Promise<Return> {
  if (typeof options === 'function') {
    // eslint-disable-next-line no-param-reassign
    callback = options;
    // eslint-disable-next-line no-param-reassign
    options = {};
  }

  if (options.existingLock?.name !== lockName) {
    if (options.existingLock) {
      console.error(
        "The passed in lock's name does not match the lock requested. Requesting a new lock for safety (this *may* cause a deadlock)",
      );
    }
    return WebLocksAvailable.then((lockManager) =>
      // eslint-disable-next-line no-restricted-syntax
      lockManager.request(lockName, options, callback).catch(e => {
        if (
          e instanceof Error &&
          (e.message.includes("'LockGrantedCallback'") ||
            e.message.includes('The provided callback is no longer runnable'))
        ) {
          return reentrantWebLocksRequest(lockName, options, callback);
        }
        throw e;
      }),
    ) as Promise<Return>;
  }
  if (options.existingLock.mode === 'shared') {
    if (options.mode !== 'shared') {
      throw new Error(
        "The passed in lock's mode is 'shared', but an exclusive lock is required. The caller should request an exclusive lock in the first place",
      );
    }
  }

  // Lock already exists and is validated to be correct, run the callback normally
  return callback(options.existingLock);
}
